import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { ImagePlus, Loader2, Pencil, Printer, Sparkles, Trash2 } from "lucide-react";
import type { ApparelStudy, CustomMockup } from "@erc/shared";
import { customApi, studioTestDesigns, useStudioModel, type CustomListingDetail } from "../../lib/customListing";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { DesignAreaEditor, type AreaRect } from "../mockup/DesignAreaEditor";
import { useSupplierVariations } from "../../lib/useSupplierVariations";

// Custom Liste Studyosu 5. adim — bos mockup olusturma. Secilen gorselden (ilham fotografi
// ya da yuklenen) urunlerin baskisiz hali AI ile uretilir ve baski alanlari bulunur; kullanici
// alanlari duzenler, "Tasarimlari yerlestir" her alani kendi kumas rengine gore basar
// (hazir mockuplarla ayni mantik).

const abs = (u: string) => (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);

export function BlankMockups({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study as ApparelStudy | null;
  const sources = [...new Set([...(study?.imageUrls ?? []), ...(rec.analysis?.imageUrls ?? [])])].slice(0, 12);
  const [src, setSrc] = useState(sources[0] ?? "");
  const [count, setCount] = useState<number>(study?.items.length || 1);
  const [model, setModel] = useStudioModel(rec);
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<CustomMockup | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const jobs = rec.template.blankMockups ?? [];
  const supplierVars = useSupplierVariations();
  const hasSamples = (rec.template.samples?.renders?.length ?? 0) > 0;
  const records = useQuery({
    queryKey: ["custom-blank-mockups", rec.id, jobs.map((j) => `${j.id}:${j.status}`).join(",")],
    queryFn: () => customApi<CustomMockup[]>("GET", `/${rec.id}/blank-mockups`),
    enabled: jobs.some((j) => j.mockupId),
  });

  const create = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/blank-mockups`, { imageUrl: src, count, model }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const place = useMutation({
    mutationFn: (mockupId: string) => customApi("POST", `/${rec.id}/mockup-renders`, { mockupIds: [mockupId] }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const removeJob = useMutation({
    mutationFn: (jobId: string) => customApi("DELETE", `/${rec.id}/blank-mockups/${jobId}`),
    onSuccess: onChanged,
  });
  const upload = async (f: File) => {
    setUploading(true); setErr(null);
    try {
      const dataUrl = await new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => bad(r.error); r.readAsDataURL(f); });
      const r = await customApi<{ url: string }>("POST", `/${rec.id}/photo`, { dataUrl });
      setSrc(r.url); setCount(1);
    } catch (e) { setErr((e as Error).message); } finally { setUploading(false); }
  };
  const saveAreas = async (m: CustomMockup, areas: AreaRect[]) => {
    const first = areas[0] as (AreaRect & { theme?: "light" | "dark" }) | undefined;
    const res = await fetch(`${getBackendUrl()}/api/mockups/${m.id}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({
        designAreas: areas, theme: first?.theme ?? "light",
        designAreaX: first ? Math.round(first.x) : 0, designAreaY: first ? Math.round(first.y) : 0,
        designAreaW: first ? Math.round(first.w) : 0, designAreaH: first ? Math.round(first.h) : 0,
        designAreaRotation: first ? Math.round(first.rotation || 0) : 0,
      }),
    });
    if (!res.ok) { setErr(`Alanlar kaydedilemedi (${res.status})`); return; }
    setEditing(null); void records.refetch();
  };
  const running = jobs.some((j) => j.status === "running");

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="mb-1 text-sm font-semibold text-gray-900">Boş mockup oluştur</h3>
      <p className="mb-3 text-xs text-gray-500">Seçtiğin görseldeki ürünlerin baskısız hali AI ile üretilir ve baskı alanları bulunur (~1 dakika, bir görsel ücreti). Alanları düzenledikten sonra "Tasarımları yerleştir": her alan kendi tişört rengine göre basılır; açı ve perspektif alanın ayarından gelir. Oluşan mockup kütüphanende de kalır.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {sources.map((u) => (
          <button key={u} onClick={() => { setSrc(u); setCount(u === study?.imageUrls?.[0] ? study?.items.length || 1 : 1); }} aria-pressed={src === u} title="Kaynak görsel"
            className={clsx("h-16 w-16 overflow-hidden rounded-lg border-2", src === u ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
            <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
        {src && !sources.includes(src) && <img src={abs(src)} alt="Yüklenen görsel" className="h-16 w-16 rounded-lg border-2 border-indigo-500 object-cover" />}
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="inline-flex h-16 items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Görsel yükle
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="blank-count" className="text-xs text-gray-600">Ürün sayısı</label>
        <input id="blank-count" type="number" min={1} max={8} value={count} onChange={(e) => setCount(Math.max(1, Math.min(8, Number(e.target.value) || 1)))}
          className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <label htmlFor="blank-model" className="sr-only">Görsel modeli</label>
        <select id="blank-model" value={model} onChange={(e) => setModel(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
          {rec.imageModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <button onClick={() => create.mutate()} disabled={!src || create.isPending || running}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {create.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {running ? "Oluşturuluyor…" : "Boş mockup oluştur"}
        </button>
      </div>
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}

      {jobs.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {jobs.slice().reverse().map((j) => {
            const m = records.data?.find((r) => r.id === j.mockupId);
            const areaCount = Array.isArray(m?.designAreas) ? m!.designAreas!.length : 0;
            return (
              <div key={j.id} className="overflow-hidden rounded-lg border border-gray-200">
                {j.url ? <img src={j.url} alt="Boş mockup" loading="lazy" className="aspect-square w-full bg-gray-50 object-contain" />
                  : <div className="flex aspect-square w-full items-center justify-center bg-gray-50 text-xs text-gray-500">{j.status === "running" ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Üretiliyor…</> : j.error ?? "Başarısız"}</div>}
                <div className="flex flex-wrap items-center gap-1.5 p-2">
                  {j.status === "ready" && m && (
                    <>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{areaCount} alan</span>
                      <button onClick={() => setEditing(m)} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                        <Pencil className="h-3.5 w-3.5" /> Alanları düzenle
                      </button>
                      <button onClick={() => place.mutate(m.id)} disabled={!hasSamples || place.isPending} title={hasSamples ? undefined : "Önce 4. adımda örnekleri bas"}
                        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                        {place.isPending && place.variables === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />} Tasarımları yerleştir
                      </button>
                    </>
                  )}
                  {j.status !== "running" && (
                    <button onClick={() => removeJob.mutate(j.id)} aria-label="Listeden kaldır" title="Listeden kaldır (mockup kütüphanede kalır)"
                      className="ml-auto rounded p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <DesignAreaEditor mockup={editing} onSave={(areas) => saveAreas(editing, areas)} onCancel={() => setEditing(null)} backLabel="Stüdyoya dön" {...studioTestDesigns(rec)}
          {...(rec.isGarment !== false ? { apparel: { vars: supplierVars, onSaved: () => { void records.refetch(); } } } : {})} />
      )}
    </div>
  );
}
